<template>
  <div :class="{ error: error, disabled: disabled }" class="cb-text-input">
    <input
      :aria-disabled="String(disabled)"
      :aria-required="required"
      :disabled="disabled"
      :id="id"
      :required="required"
      :type="isPasswordShowing || type"
      :value="value"
      :maxlength="maxLength"
      :data-tab-order="tabOrder"
      @blur="updateFocus('blur')"
      @focus="updateFocus('focus')"
      @input="updateValue($event.target.value)"
      class="cb-text-input__input cb-body-1"
      :ref="id"
    />
    <label
      :class="{ active: value.length > 0 }"
      :for="id"
      class="cb-text-input__label cb-body-1"
      >{{ label }}</label
    >
    <div class="cb-text-input__error-icon"></div>
    <div class="cb-text-input__error-bar"></div>
    <button
      @click="togglePassword"
      v-if="type === 'password'"
      type="button"
      :aria-label="isPasswordShowing ? 'Hide Password' : 'Show Password'"
      class="cb-text-input__toggle-password"
    >
      <img
        v-show="!isPasswordShowing"
        alt
        src="../../assets/icons/show-eye.svg"
      />
      <img
        v-show="isPasswordShowing"
        alt
        src="../../assets/icons/hide-eye.svg"
      />
    </button>
    <button
      @click.prevent="handleEditIconClick"
      :aria-label="`Edit ${label}`"
      :disabled="requestInProgress"
      type="button"
      class="cb-text-input__edit-icon"
      v-if="disabled"
    ></button>
    <span class="sr-only">{{ errorText }}</span>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    id: {
      type: String,
    },
    type: {
      type: String,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorText: {
      type: String,
    },
    isDisabled: {
      type: Boolean,
    },
    maxLength: {
      type: Number,
    },
    tabOrder: {
      type: String,
    },
  },
  data() {
    return {
      disabled: this.isDisabled,
      isPasswordShowing: false,
    };
  },
  computed: {
    inputType() {
      let type;
      if (this.type === 'password') {
        if (this.isPasswordShowing) {
          type = 'text';
        } else {
          type = 'password';
        }
      } else {
        type = this.type;
      }
      return type;
    },
    ...mapState({
      requestInProgress: (state) => state.requestInProgress,
    }),
  },
  methods: {
    updateValue(value) {
      this.$emit('input', value);
    },
    updateFocus(event) {
      if (event === 'blur') {
        this.$emit('blur');
        if (this.isDisabled) {
          this.disabled = true;
        }
      }
      if (event === 'focus') this.$emit('focus');
    },
    handleEditIconClick() {
      this.disabled = false;
    },
    togglePassword() {
      this.isPasswordShowing = !this.isPasswordShowing;
    },
  },
  watch: {
    requestInProgress() {
      if (this.requestInProgress === true) {
        this.disabled = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cb-text-input {
  position: relative;
  display: flex;
  flex-grow: 1;

  &__input {
    border: none;
    width: 100%;
    box-sizing: border-box;
    background-color: $lighter-grey;
    padding: 19px 50px 7px 16px;

    &:focus {
      ~ label {
        transform: translateY(-100%) scale(0.75);
      }
    }

    &::-ms-reveal,
    &::-ms-clear {
      display: none;
    }
  }

  &__label {
    position: absolute;
    top: 50%;
    user-select: none;
    left: 16px;
    transition: transform 0.2s ease, color 0.2s ease;
    transform: translateY(-50%);
    transform-origin: 0 0;
    color: #000;
    opacity: 1;
    cursor: text;

    &.active {
      transform: translateY(-100%) scale(0.75);
    }
  }

  &__error-icon {
    position: absolute;
    height: 18px;
    width: 18px;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.2s ease;

    &::after,
    &::before {
      position: absolute;
      content: ' ';
      height: 24px;
      width: 3px;
      top: -3px;
      right: 8px;
      background-color: $dark-red;
    }

    &::before {
      transform: rotate(45deg);
    }
    &::after {
      transform: rotate(-45deg);
    }
  }

  &__error-bar {
    position: absolute;
    background-color: $dark-red;
    transition: background-color 0.2s ease-in;
    height: 100%;
    width: 5px;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &__edit-icon {
    position: absolute;
    content: ' ';
    border: none;
    background-color: white;
    height: 16px;
    width: 16px;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    background-image: url('../../assets/icons/edit-icon.svg');

    &[disabled] {
      cursor: default;
    }
  }

  &__toggle-password {
    position: absolute;
    height: 30px;
    width: 30px;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    transition: right 0.2s ease;
    border: 0;
    padding: 0;
    background-color: transparent;

    img {
      width: 100%;
    }
  }

  &.error {
    .cb-text-input {
      &__error-icon,
      &__error-bar {
        opacity: 1;
      }

      &__label {
        color: $dark-red;
      }
    }

    .cb-text-input__toggle-password {
      right: 40px;
    }
  }

  &.disabled {
    .cb-text-input__input {
      background-color: white;
    }
  }
}
</style>
